import { FormikProps } from "formik";
import { UserFormValues } from "./userValidation";

/**
 * Marks all user fields as touched to show validation errors
 * @param formik - Formik instance
 */
export const touchAllUserFields = (formik: FormikProps<UserFormValues>): void => {
  Object.keys(formik.values).forEach((field) => {
    formik.setFieldTouched(field, true);
  });
};

/**
 * Prepares user payload for API submission
 * @param values - Form values
 * @returns Cleaned payload for API
 */
export const prepareUserPayload = (values: UserFormValues) => {
  const payload = { ...values };
  
  // Remove merchantId for internal roles or if not provided
  if (payload.role?.startsWith("INTERNAL") || !payload.merchantId) {
    delete payload.merchantId;
  }
  
  return payload;
};

/**
 * Gets the appropriate error message for a field
 * @param formik - Formik instance
 * @param fieldName - Name of the field
 * @returns string | undefined - Error message if exists and field is touched
 */
export const getUserFieldError = (
  formik: FormikProps<UserFormValues>,
  fieldName: keyof UserFormValues
): string | undefined => {
  const touched = formik.touched[fieldName];
  const error = formik.errors[fieldName];
  
  return touched && error ? String(error) : undefined;
};

/**
 * Validates the entire form and touches all fields to show errors
 * @param formik - Formik instance
 * @returns Promise<boolean> - true if valid, false if has errors
 */
export const validateUserForm = async (formik: FormikProps<UserFormValues>): Promise<boolean> => {
  const errors = await formik.validateForm();
  touchAllUserFields(formik);
  return Object.keys(errors).length === 0;
};
